import type { IconName } from "./icons.js";

export const TAB_GROUPS = [
  { label: "Fleet", tabs: ["chat"] },
  {
    label: "Control",
    tabs: ["overview", "channels", "instances", "sessions", "cron", "llm"],
  },
  { label: "Agent", tabs: ["agents", "employees"] },
  {
    label: "Settings",
    tabs: ["config", "developer", "debug", "logs"],
  },
  { label: "Help", tabs: ["help"] },
] as const;

export type LogSubTab = "openclaw-logs" | "jaisiu-logs" | "gateway-logs" | "docker-logs";
/**
 * JAISIU-pipeline-3427 — public/internal base-path alias.
 *
 * Historically the deployment portal exposed the control UI under
 * `/apps/openclaw/...` (the legacy product name) while the new
 * internal config object referenced it as `apps/jaisiu`. The portal
 * address is what external users (and bookmarks, and CI links) hit;
 * the internal name is what the rest of the app uses when persisting
 * settings, computing share links, and serialising the routing state.
 *
 * The three routing helpers (`pathForTab`, `tabFromPath`,
 * `inferBasePathFromPathname`) all round-trip through this map so a
 * portal URL stays canonical on the wire while internal config stays
 * stable across the rename.
 */
const PUBLIC_BASE_SEGMENTS = ["openclaw"] as const;
const INTERNAL_BASE_SEGMENTS = ["jaisiu"] as const;

function internalToPublicBasePath(basePath: string): string {
  if (!basePath) return basePath;
  const segments = basePath.split("/").filter(Boolean);
  if (segments.length === 0) return basePath;
  const out = segments.map((seg) => {
    if (seg === INTERNAL_BASE_SEGMENTS[0]) return PUBLIC_BASE_SEGMENTS[0];
    return seg;
  });
  return "/" + out.join("/");
}

function publicToInternalBasePath(basePath: string): string {
  if (!basePath) return basePath;
  const segments = basePath.split("/").filter(Boolean);
  if (segments.length === 0) return basePath;
  const out = segments.map((seg) => {
    if (seg === PUBLIC_BASE_SEGMENTS[0]) return INTERNAL_BASE_SEGMENTS[0];
    return seg;
  });
  return "/" + out.join("/");
}

export type Tab =
  | "agents"
  | "overview"
  | "channels"
  | "instances"
  | "sessions"
  | "llm-stats"
  | "local-llm"
  | "llm"
  | "cron"
  | "skills"
  | "employees"
  | "workforce"
  | "chat"
  | "config"
  | "developer"
  | "debug"
  | "logs"
  | "openclaw-logs"
  | "jaisiu-logs"
  | "gateway-logs"
  | "docker-logs"
  | "help"
  | "network";

/** Primary tabs pinned to the mobile bottom bar (≤768px). */
export const MOBILE_BOTTOM_TABS = [
  "chat",
  "overview",
  "channels",
  "config",
] as const satisfies readonly Tab[];

export type MobileBottomTab = (typeof MOBILE_BOTTOM_TABS)[number];

export function isMobileBottomTab(tab: Tab): tab is MobileBottomTab {
  return (MOBILE_BOTTOM_TABS as readonly Tab[]).includes(tab);
}

export type EmployeeDetailPath = `/employees/${string}`;

/** Fleet Control chat console tab. */
export function isFleetConsoleTab(tab: Tab): boolean {
  return tab === "chat";
}

const TAB_PATHS: Record<Tab, string> = {
  agents: "/agents",
  overview: "/overview",
  channels: "/channels",
  instances: "/instances",
  sessions: "/sessions",
  "llm-stats": "/llm-stats",
  "local-llm": "/local-llm",
  cron: "/cron",
  skills: "/skills",
  employees: "/employees",
  workforce: "/workforce",
  chat: "/chat",
  config: "/config",
  developer: "/developer",
  debug: "/debug",
  logs: "/logs",
  "openclaw-logs": "/openclaw-logs",
  "jaisiu-logs": "/jaisiu-logs",
  "gateway-logs": "/gateway-logs",
  "docker-logs": "/docker-logs",
  help: "/help",
  llm: "/llm",
  network: "/network",
};

const PATH_TO_TAB = new Map(
  Object.entries(TAB_PATHS)
    .filter(([tab]) => tab !== "workforce")
    .map(([tab, path]) => [path, tab as Tab]),
);

export function normalizeBasePath(basePath: string): string {
  if (!basePath) {
    return "";
  }
  let base = basePath.trim();
  if (!base.startsWith("/")) {
    base = `/${base}`;
  }
  if (base === "/") {
    return "";
  }
  if (base.endsWith("/")) {
    base = base.slice(0, -1);
  }
  return base;
}

export function normalizePath(path: string): string {
  if (!path) {
    return "/";
  }
  let normalized = path.trim();
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}

export function pathForTab(tab: Tab, basePath = ""): string {
  const base = normalizeBasePath(internalToPublicBasePath(basePath));
  const path = TAB_PATHS[tab];
  return base ? `${base}${path}` : path;
}

export function pathForEmployeeDetail(employeeId: string, basePath = ""): string {
  const base = normalizeBasePath(basePath);
  const path = `/employees/${employeeId}`;
  return base ? `${base}${path}` : path;
}

export function employeeIdFromPath(pathname: string, basePath = ""): string | null {
  const base = normalizeBasePath(internalToPublicBasePath(basePath));
  let path = pathname || "/";
  if (base) {
    if (path.startsWith(`${base}/`)) {
      path = path.slice(base.length);
    }
  }
  const normalized = normalizePath(path);
  if (normalized.startsWith("/employees/") && normalized.length > "/employees/".length) {
    return normalized.slice("/employees/".length);
  }
  return null;
}

export function tabFromPath(pathname: string, basePath = ""): Tab | null {
  // Accept either the internal OR the public spelling of basePath so a
  // caller passing "apps/jaisiu" still matches a URL path that lives
  // under "apps/openclaw". We compute the public-spelling equivalent
  // so the prefix-strip below succeeds against either layout.
  const internalBase = normalizeBasePath(basePath);
  const publicBase = internalToPublicBasePath(internalBase);
  let path = pathname || "/";
  // Try both spellings; whichever prefix matches wins.
  const bases = [internalBase, publicBase].filter((b) => b && b !== "/");
  for (const base of bases) {
    if (path === base) {
      path = "/";
      break;
    }
    if (path.startsWith(`${base}/`)) {
      path = path.slice(base.length);
      break;
    }
  }
  let normalized = normalizePath(path).toLowerCase();
  if (normalized.endsWith("/index.html")) {
    normalized = "/";
  }
  if (normalized === "/") {
    return "chat";
  }

  if (normalized === "/help" || normalized.startsWith("/help/")) {
    return "help";
  }

  // Handle employee detail paths
  if (normalized.startsWith("/employees/") && normalized.length > "/employees/".length) {
    return "employees";
  }

  // Legacy Fleet Control Beta URL → main Fleet Control
  if (normalized === "/fleet-control-beta") {
    return "chat";
  }

  // Legacy Usage/Spend tabs and pre-merge sub-tabs → unified LLM page
  if (
    normalized === "/usage" ||
    normalized === "/spend" ||
    normalized === "/stats" ||
    normalized === "/llm-stats" ||
    normalized === "/local-llm"
  ) {
    return "llm";
  }

  // Handle skills/prompts sub-routes if needed in the future
  // if (normalized.includes("/skills") || normalized.includes("/prompts")) {
  //   return "employees";
  // }

  return PATH_TO_TAB.get(normalized) ?? null;
}

export function inferBasePathFromPathname(pathname: string): string {
  // When the operator visits a portal URL (e.g. /apps/openclaw/cron)
  // we want to surface the *internal* basePath (e.g. /apps/jaisiu) so
  // the rest of the app stores a canonical config name. Run the
  // mapping here, then return whatever prefixes match a known tab.
  let normalized = normalizePath(pathname);
  if (normalized.endsWith("/index.html")) {
    normalized = normalizePath(normalized.slice(0, -"/index.html".length));
  }
  if (normalized === "/") {
    return "";
  }
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "";
  }
  for (let i = 0; i < segments.length; i++) {
    const candidate = `/${segments.slice(i).join("/")}`.toLowerCase();
    if (PATH_TO_TAB.has(candidate)) {
      const prefix = segments.slice(0, i);
      const joined = prefix.length ? `/${prefix.join("/")}` : "";
      return publicToInternalBasePath(joined);
    }
    if (candidate === "/help" || candidate.startsWith("/help/")) {
      const prefix = segments.slice(0, i);
      const joined = prefix.length ? `/${prefix.join("/")}` : "";
      return publicToInternalBasePath(joined);
    }
  }
  const joinedAll = `/${segments.join("/")}`;
  return publicToInternalBasePath(joinedAll);
}

export function iconForTab(tab: Tab): IconName {
  switch (tab) {
    case "agents":
      return "folder";
    case "chat":
      return "messageSquare";
    case "overview":
      return "barChart";
    case "channels":
      return "link";
    case "instances":
      return "radio";
    case "sessions":
      return "fileText";
    case "llm-stats":
      return "barChart";
    case "local-llm":
      return "bot";
    case "cron":
      return "loader";
    case "skills":
      return "zap";
    case "employees":
      return "users";
    case "workforce":
      return "workflow";
    case "config":
      return "settings";
    case "developer":
      return "wrench";
    case "debug":
      return "bug";
    case "logs":
    case "openclaw-logs":
    case "jaisiu-logs":
    case "gateway-logs":
    case "docker-logs":
      return "scrollText";
    case "help":
      return "book";
    case "llm":
      return "barChart";
    default:
      return "folder";
  }
}

export function titleForTab(tab: Tab) {
  switch (tab) {
    case "agents":
      return "Agents";
    case "overview":
      return "Overview";
    case "channels":
      return "Channels";
    case "instances":
      return "Instances";
    case "sessions":
      return "Sessions";
    case "llm":
    case "llm-stats":
    case "local-llm":
      return "LLM";
    case "cron":
      return "Cron Jobs";
    case "skills":
      return "Skills";
    case "employees":
      return "Remote Workers";
    case "workforce":
      return "Workflow";
    case "chat":
      return "Fleet Control";
    case "config":
      return "Config";
    case "developer":
      return "Developer";
    case "debug":
      return "Debug";
    case "logs":
      return "Logs";
    case "openclaw-logs":
      return "OpenClaw Logs";
    case "jaisiu-logs":
      return "Jaisiu Logs";
    case "gateway-logs":
      return "Gateway Logs";
    case "docker-logs":
      return "Docker Logs";
    case "help":
      return "Help";
    case "network":
      return "Network";
    default:
      return "Control";
  }
}

export function shortLabelForTab(tab: Tab): string {
  switch (tab) {
    case "chat":
      return "Fleet";
    case "overview":
      return "Home";
    case "workforce":
      return "Fleet";
    case "channels":
      return "Channels";
    case "config":
      return "Settings";
    case "agents":
      return "Agents";
    case "employees":
      return "Staff";
    case "sessions":
      return "Sessions";
    case "instances":
      return "Nodes";
    case "llm":
    case "llm-stats":
    case "local-llm":
      return "LLM";
    case "cron":
      return "Cron";
    case "skills":
      return "Skills";
    case "developer":
      return "Dev";
    case "debug":
      return "Debug";
    case "logs":
      return "Logs";
    case "openclaw-logs":
      return "OpenClaw Logs";
    case "jaisiu-logs":
      return "Jaisiu Logs";
    case "gateway-logs":
      return "Gateway Logs";
    case "docker-logs":
      return "Docker Logs";
    default:
      return titleForTab(tab).split(" ")[0] ?? titleForTab(tab);
  }
}

export function subtitleForTab(tab: Tab) {
  switch (tab) {
    case "agents":
      return "Manage agent workspaces, tools, and identities.";
    case "overview":
      return "Jaisiu status, entry points, and a fast health read.";
    case "channels":
      return "Manage channels and settings.";
    case "instances":
      return "Presence beacons from connected clients and nodes.";
    case "sessions":
      return "Inspect active sessions and adjust per-session defaults.";
    case "llm-stats":
      return "Usage, spend, tokens, costs, and savings across your fleet.";
    case "local-llm":
      return "Ollama status, installed models, and a read-only activity console.";
    case "cron":
      return "Schedule wakeups and recurring agent runs.";
    case "skills":
      return "Manage skill availability and API key injection.";
    case "employees":
      return "Manage virtual employees and HR system synchronization.";
    case "workforce":
      return "Design automation flows: skills, routing rules, delegation, and analytics.";
    case "chat":
      return "Jaisiu • direct intervention console";
    case "config":
      return "Edit configuration safely.";
    case "developer":
      return "Build Control UI assets on the gateway host and stream output.";
    case "debug":
      return "Gateway snapshots, events, and manual RPC calls.";
    case "logs":
      return "Live tail of the gateway file logs.";
    case "openclaw-logs":
      return "Session output logs from ~/.openclaw";
    case "jaisiu-logs":
      return "Session output logs from ~/.jaisiu";
    case "gateway-logs":
      return "Gateway file logs (alternate view)";
    case "docker-logs":
      return "Live docker container logs";
    case "help":
      return "How to use each Fleet Control section.";
    case "llm":
      return "Connect providers, assign models to roles, and review usage in one place.";
    case "network":
      return "Discover and compare other Jaisiu instances sharing the registry.";
    default:
      return "";
  }
}
